import { useState } from "react";
import { Bot, Loader2, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import { handoff } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { Conversation } from "@/types";

interface HandoffToggleButtonProps {
  conversation: Conversation;
  token: string | undefined;
  refresh: () => Promise<void>;
  iconOnly?: boolean;
}

export function HandoffToggleButton({
  conversation,
  token,
  refresh,
  iconOnly = false,
}: HandoffToggleButtonProps) {
  const [pending, setPending] = useState(false);
  const isHuman = conversation.status === "human";

  async function handleClick() {
    setPending(true);
    try {
      if (isHuman) {
        await handoff.release({ leadId: conversation.lead.id, token });
        toast.success("Devolviste el lead a Allison");
      } else {
        await handoff.takeOver({ leadId: conversation.lead.id, token });
        toast.success("Tomaste la conversación");
      }
      // Refresh aislado: si falla NO debe mostrar error de handoff (la acción
      // ya fue exitosa server-side; el toast de éxito ya se mostró).
      try {
        await refresh();
      } catch (refreshErr) {
        console.error(
          "[HandoffToggleButton] refresh post-handoff failed",
          refreshErr
        );
      }
    } catch {
      toast.error(
        isHuman ? "No pudimos devolver el lead" : "No pudimos tomar el lead",
        { id: "handoff-error" }
      );
    } finally {
      setPending(false);
    }
  }

  const Icon = pending ? Loader2 : isHuman ? Bot : UserPlus;
  const longLabel = isHuman ? "Devolver a Allison" : "Asignar humano";
  const shortLabel = isHuman ? "Devolver" : "Asignar";

  if (iconOnly) {
    return (
      <button
        onClick={handleClick}
        disabled={pending}
        aria-label={longLabel}
        className="flex size-10 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-surface-2 active:bg-surface-2 disabled:opacity-60"
      >
        <Icon className={cn("size-5", pending && "animate-spin")} />
      </button>
    );
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={pending}
      onClick={handleClick}
    >
      <Icon className={cn("size-4", pending && "animate-spin")} />
      <span className="hidden sm:inline">{longLabel}</span>
      <span className="sm:hidden">{shortLabel}</span>
    </Button>
  );
}
